import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { Link } from "react-router-dom";
import { Alert, Badge, Card, CardBody, CardTitle, Col, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import { EMPTY_LIST } from "src/common/constants";
import ControlService from "src/modules/data-protection/apis/ControlService";
import { formatTaskAndRiskId } from "src/modules/data-protection/constants";
import {
  CONTROL_MAPPING_TYPE_COLORS,
  CONTROL_MAPPING_TYPE_ORDER,
  RISK_LEVEL_DISPLAY,
} from "src/modules/data-protection/constants/Common";
import {
  formatControlNumber,
  getPolicyStatus,
  getRiskLevelKey,
  isTisaxFramework,
} from "src/modules/data-protection/helpers/Controls";

const getEmbeddedTasks = (control) => {
  if (Array.isArray(control?.tasks_list)) {
    return control.tasks_list;
  }

  const tasks = control?.related_tasks ?? control?.tasks;
  return Array.isArray(tasks) ? tasks : null;
};

const getTasksActionLabel = (control, t) => {
  const tasks = control?.tasks;

  if (!tasks || Array.isArray(tasks)) {
    return t("View tasks");
  }

  const openCount =
    Number(tasks.new || 0) +
    Number(tasks.in_progress || 0) +
    Number(tasks.review || 0);

  if (openCount > 0) {
    return `${openCount} ${t("tasks")}`;
  }

  const totalCount =
    openCount +
    Number(tasks.closed || 0) +
    Number(tasks.overdue || 0);

  if (totalCount > 0) {
    return t("View tasks");
  }

  return t("View tasks");
};

const RelatedTasksLinks = ({ controlId, tasks }) => {
  if (!tasks?.length) {
    return "-";
  }

  return (
    <div
      style={{
        display: "inline-block",
        width: "100%",
        wordBreak: "break-word",
        whiteSpace: "normal",
      }}
    >
      {tasks.map((task, index) => {
        const taskId = task.id ?? task.task_id;
        const tooltipId = `control-health-${controlId}-task-${taskId ?? index}`;
        const taskLabel = task.name ?? task.title;

        return (
          <Fragment key={taskId ?? index}>
            <Link
              id={tooltipId}
              to={`/admin/data-protection/tasks/${taskId}/details?controlId=${controlId}`}
              className="text-dark"
            >
              {formatTaskAndRiskId(
                "task",
                task.questionnaire === "software"
                  ? task.asset_id
                  : task.case_secondary_id,
                task.risk_secondary_id ?? task.secondary_id,
                task.is_mitigation,
                task.questionnaire ?? "control",
                task.secondary_id ?? task.task_secondary_id
              )}
            </Link>
            {index < tasks.length - 1 ? <span className="me-1">,</span> : null}
            {taskLabel ? (
              <UncontrolledTooltip target={tooltipId} trigger="hover">
                {taskLabel}
              </UncontrolledTooltip>
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
};

const ControlHealthTasksCell = ({
  control,
  t,
  embeddedTasks,
  loadedTasks,
  isLoading,
  onLoadTasks,
}) => {
  if (embeddedTasks?.length) {
    return (
      <RelatedTasksLinks controlId={control.control_id} tasks={embeddedTasks} />
    );
  }

  if (loadedTasks !== undefined) {
    return (
      <RelatedTasksLinks
        controlId={control.control_id}
        tasks={loadedTasks}
      />
    );
  }

  if (isLoading) {
    return <Spinner size="sm" />;
  }

  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0 align-baseline text-dark"
      onClick={() => onLoadTasks(control.control_id)}
    >
      {getTasksActionLabel(control, t)}
    </button>
  );
};

const typeBadgeStyle = {
  fontSize: 11,
  lineHeight: 1,
  padding: "4px 10px",
  borderRadius: 999,
  fontWeight: 600,
  textTransform: "capitalize",
};

const formatMappingTypeLabel = (type) => {
  const normalized = String(type || "").toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getMappingTypeCounts = (row) => {
  const byCount = row?.mapping_types_by_count;

  if (byCount && typeof byCount === "object") {
    return CONTROL_MAPPING_TYPE_ORDER.reduce((counts, type) => {
      counts[type] = Number(byCount[type] ?? 0);
      return counts;
    }, {});
  }

  return null;
};

const MappingTypeBadgesCell = ({ control, t }) => {
  const typeCounts = getMappingTypeCounts(control);
  const tooltipId = `control-mapping-types-${control.control_id}`;

  if (!typeCounts) {
    return "-";
  }

  const activeTypes = CONTROL_MAPPING_TYPE_ORDER.filter(
    (type) => Number(typeCounts[type] ?? 0) > 0
  );

  if (!activeTypes.length) {
    return "-";
  }

  return (
    <>
      <span
        id={tooltipId}
        className="d-inline-flex flex-wrap gap-1"
        style={{ cursor: "default" }}
      >
        {activeTypes.map((type) => (
          <Badge
            key={type}
            pill
            style={{
              ...typeBadgeStyle,
              backgroundColor: CONTROL_MAPPING_TYPE_COLORS[type] || "#ADB5BD",
              color: "#fff",
            }}
          >
            {t(formatMappingTypeLabel(type))}
          </Badge>
        ))}
      </span>
      <UncontrolledTooltip target={tooltipId} trigger="hover">
        {CONTROL_MAPPING_TYPE_ORDER.map((type) => (
          <div key={type}>
            {t(formatMappingTypeLabel(type))}: {Number(typeCounts[type] ?? 0)}
          </div>
        ))}
      </UncontrolledTooltip>
    </>
  );
};

const ControlHealthSection = ({
  t,
  dashboardData,
  frameworkLabelMap,
  selectedFramework,
  isLoading = false,
  isError = false,
}) => {
  const controlsTable = dashboardData?.controls_table || [];
  const [loadedTasksByControlId, setLoadedTasksByControlId] = useState({});
  const [loadingControlId, setLoadingControlId] = useState(null);

  useEffect(() => {
    setLoadedTasksByControlId({});
    setLoadingControlId(null);
  }, [dashboardData]);

  const handleLoadTasks = useCallback(
    async (controlId) => {
      if (
        loadedTasksByControlId[controlId] !== undefined ||
        loadingControlId === controlId
      ) {
        return;
      }

      setLoadingControlId(controlId);

      try {
        const service = ControlService.getInstance();
        const data = await service.fetchTasks(controlId, {
          pageIndex: 1,
          pageSize: 100,
        });

        setLoadedTasksByControlId((current) => ({
          ...current,
          [controlId]: data?.tasks || [],
        }));
      } catch {
        setLoadedTasksByControlId((current) => ({
          ...current,
          [controlId]: [],
        }));
      } finally {
        setLoadingControlId(null);
      }
    },
    [loadedTasksByControlId, loadingControlId]
  );

  const formatMappingTargets = (targets = []) => {
    if (!targets.length) return "-";
    return targets
      .map((target) => frameworkLabelMap[target] || target)
      .join(", ");
  };

  const renderRiskCell = (control) => {
    const riskKey = getRiskLevelKey(control);

    if (!riskKey) {
      return "-";
    }

    const riskConfig = RISK_LEVEL_DISPLAY[riskKey] || {
      color: "#F5A14B",
      label: riskKey.charAt(0).toUpperCase() + riskKey.slice(1),
    };

    return (
      <span className="d-inline-flex align-items-center gap-1">
        <span style={{ color: riskConfig.color, fontSize: 14 }} aria-hidden="true">
          ●
        </span>
        {t(riskConfig.label)}
      </span>
    );
  };

  const renderPolicyCell = (control) => {
    return getPolicyStatus(control) === "attached" ? (
      <span className="text-primary">{t("Attached")}</span>
    ) : (
      <span className="text-danger">{t("Missing")}</span>
    );
  };

  const paginationOptions = useMemo(
    () => ({
      hideSizePerPage: false,
      hidePageListOnlyOnePage: false,
      sizePerPageList: [
        { text: "5th", value: 5 },
        { text: "10th", value: 10 },
        { text: "All", value: controlsTable.length || 1 },
      ],
    }),
    [controlsTable.length, t]
  );

  const tableColumns = useMemo(() => {
    return [
      {
        dataField: "control_number",
        text: t("Control Number"),
        sort: false,
        formatter: (_cell, row) =>
          formatControlNumber(row.control_number, row.framework),
      },
      {
        dataField: "title",
        text: t("Title"),
        sort: false,
      },
      {
        dataField: "framework",
        text: t("Framework"),
        sort: false,
        formatter: (cell) => frameworkLabelMap[cell] || cell || "-",
      },
      {
        dataField: "applicable",
        text: t("Applicable"),
        sort: false,
        formatter: (_cell, row) => {
          const notApplicableTooltipId = `control-not-applicable-${row.control_id}`;

          if (row.applicable) {
            return (
              <span className="d-inline-flex align-items-center gap-1">
                <i className="ri-check-line text-success" />
                {t("Yes")}
              </span>
            );
          }

          return (
            <>
              <span
                id={row.not_applicable_reason ? notApplicableTooltipId : undefined}
                className="d-inline-flex align-items-center gap-1"
              >
                <i className="ri-close-line text-danger" />
                {t("No")}
              </span>
              {row.not_applicable_reason ? (
                <UncontrolledTooltip target={notApplicableTooltipId}>
                  {row.not_applicable_reason}
                </UncontrolledTooltip>
              ) : null}
            </>
          );
        },
      },
      ...(isTisaxFramework(selectedFramework)
        ? [
            {
              dataField: "maturity_level",
              text: t("Maturity"),
              sort: false,
              formatter: (cell) => cell || 0,
            },
          ]
        : []),
      {
        dataField: "tasks",
        text: t("Tasks"),
        sort: false,
        formatter: (_cell, row) => (
          <ControlHealthTasksCell
            control={row}
            t={t}
            embeddedTasks={getEmbeddedTasks(row)}
            loadedTasks={loadedTasksByControlId[row.control_id]}
            isLoading={loadingControlId === row.control_id}
            onLoadTasks={handleLoadTasks}
          />
        ),
      },
      {
        dataField: "mappings_count",
        text: t("Mappings"),
        sort: false,
        formatter: (cell) => cell || 0,
      },
      {
        dataField: "mapping_types_by_count",
        text: t("Type"),
        sort: false,
        formatter: (_cell, row) => <MappingTypeBadgesCell control={row} t={t} />,
      },
      {
        dataField: "top_mapping_targets",
        text: t("Targets"),
        sort: false,
        formatter: (cell) => formatMappingTargets(cell || []),
      },
      {
        dataField: "risk_level",
        text: t("Risks"),
        sort: false,
        formatter: (_cell, row) => renderRiskCell(row),
      },
      {
        dataField: "policy_status",
        text: t("Policy"),
        sort: false,
        formatter: (_cell, row) => renderPolicyCell(row),
      },
      // {
      //   dataField: "actions",
      //   text: t("Actions"),
      //   sort: false,
      //   formatter: (_cell, row) => (
      //     <div className="d-flex align-items-center gap-2 text-muted">
      //       <Link
      //         to={`/admin/data-protection/controls/${row.control_id}/tasks`}
      //         className="text-muted"
      //         title={t("View")}
      //       >
      //         <i className="ri-eye-line" />
      //       </Link>
      //       <Link
      //         to={`/admin/data-protection/controls/${row.control_id}/tasks`}
      //         className="text-muted"
      //         title={t("Upload")}
      //       >
      //         <i className="ri-upload-cloud-line" />
      //       </Link>
      //     </div>
      //   ),
      // },
    ];
  }, [
    t,
    frameworkLabelMap,
    loadedTasksByControlId,
    loadingControlId,
    handleLoadTasks,
    selectedFramework,
  ]);

  const NoDataIndication = () => (
    <div className="alert m-0" role="alert">
      <p className="mb-0 text-center">{t(EMPTY_LIST)}</p>
    </div>
  );

  return (
    <Row>
      <Col lg="12">
        <Card className="card_box_Shadow">
          <CardBody>
            <CardTitle>{t("Control Health")}</CardTitle>

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner size="sm" className="me-2" />
                {t("Loading")}...
              </div>
            ) : null}

            {isError ? (
              <Alert color="danger" className="mb-0">
                {t("Unable to load control health data at this time.")}
              </Alert>
            ) : null}

            {!isLoading && !isError ? (
              <div
                className="table-responsive dissco-latest-cases"
                style={{ overflowX: "auto", maxWidth: "100%" }}
              >
                <BootstrapTable
                  keyField="control_id"
                  responsive
                  bordered={false}
                  data={controlsTable}
                  columns={tableColumns}
                  classes="table tpdd-table mb-4 align-middle"
                  headerWrapperClasses="thead-light"
                  noDataIndication={() => <NoDataIndication />}
                  pagination={paginationFactory(paginationOptions)}
                  options={{
                    hideSizePerPage: true,
                  }}
                />
              </div>
            ) : null}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default ControlHealthSection;
